import { memo, useEffect, useMemo, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Box, CircularProgress } from '@material-ui/core';
import { ArrowRight } from '@material-ui/icons';
import ClearIcon from '@mui/icons-material/Clear';
import moment from 'moment';
import { StyledTableIconButton } from 'components/AccountingNotesList/AccountingNotes.css';
import ControlledSelect from 'components/ControlledSelect';
import ControlledTextField from 'components/ControlledTextField';
import ControlledTimePicker from 'components/ControlledTimePicker';
import { useReportingAvailability } from 'modules/OrderReporting/useReportingAvailability';
import {
  decorateAvailabilityOptions,
  getReportingModeForDate,
} from 'modules/OrderReporting/utils';
import { isTimeOffProjectCategory } from 'utils/projectCategories';
import { parseWorkingHours } from 'utils/reportingMode';
import OrderCell from './OrderCell';
import {
  getBankHolidayTooltip,
  getFullReportDayTimes,
  isDayWithoutWorkRow,
  keepsReportedWork,
} from './tableUtils';
import { DayProjectProps, FullReportDay, FullReportRowProps } from './types';
import {
  AddIcon,
  ClearButton,
  DateWrapper,
  FileCounter,
  HeaderTooltipText,
  HiddenInput,
  InfoTooltipIcon,
  OrderWarningIcon,
  OverlappingHoursTooltip,
  RemoveIcon,
  ReportTypography,
  TableActionCell,
  TableCell,
  TableCellWithIcon,
  TableRow,
  Tooltip,
  UploadFileIcon,
  WarningIcon,
  WarningTooltip,
} from './Table.css';

const isOrderUnavailable = (option: object) =>
  (option as { orderUnavailable?: boolean }).orderUnavailable === true;

const FullReportRow = ({
  viewedUsername,
  orderInScope,
  hoursOnly = false,
  showOrder = true,
  showMultiplier = true,
  reportingMode,
  exemptClientIds,
  recordsDaysWithoutWork = false,
  isFreeDay,
  isToday,
  index,
  entry,
  rowRef,
  control,
  register,
  isLeaveDay,
  orderPending,
  clientOrProjectChanged = false,
  savedReportsWork = false,
  savedDay,
  clientsData,
  projectOptions,
  invalidWorktimeTitle,
  isInvalidWorktime,
  onInvalidWorktime,
  checkWhatToRender,
  defaultProject,
  handleClientChange,
  handleProjectChange,
  isTimesheetDisabled,
  addEntryToDailytime,
  handleFileUploadClick,
  deleteEntryFromDailytime,
  handleRefreshAfterTimeChange,
  setValue,
}: FullReportRowProps) => {
  const intl = useIntl();

  const [isEndingTimeBeforeStarting, setIsEndingTimeBeforeStarting] =
    useState(false);

  const project = useWatch({
    control,
    name: `days[${index}].project`,
  }) as DayProjectProps;

  const client = useWatch({
    control,
    name: `days[${index}].client`,
  }) as { id?: number; value?: number };

  const startTime = useWatch({
    control,
    name: `days[${index}].startTime`,
  }) as DayProjectProps;

  const endTime = useWatch({
    control,
    name: `days[${index}].endTime`,
  }) as DayProjectProps;

  const workingHours = useWatch({
    control,
    name: `days[${index}].hours`,
  });

  const reportsWorkNow = keepsReportedWork(
    { startTime, endTime, hours: workingHours, project },
    hoursOnly,
  );

  // OFF (or a date before the effective date) needs no eligibility check. A
  // weekend or holiday row needs one once it reports work: the save links it
  // to the covering assignment like any other day (U10).
  // A date out of scope for the viewed account (amendment A1) works as OFF.
  const dateMode =
    orderInScope === false
      ? 'OFF'
      : getReportingModeForDate(reportingMode, entry.date);
  const availability = useReportingAvailability(
    entry.date,
    !isTimesheetDisabled &&
      (!isFreeDay || reportsWorkNow) &&
      dateMode !== 'OFF',
    viewedUsername,
  );
  const hasCompleteAvailability =
    availability.status === 'ready' || availability.status === 'empty';
  const clientOptions = useMemo(
    () =>
      decorateAvailabilityOptions(
        clientsData ?? [],
        hasCompleteAvailability
          ? availability.clients.map(({ id }) => id)
          : undefined,
        dateMode,
        (option) => Boolean(exemptClientIds?.has(option.value)),
        recordsDaysWithoutWork,
      ),
    [
      availability.clients,
      clientsData,
      dateMode,
      exemptClientIds,
      hasCompleteAvailability,
      recordsDaysWithoutWork,
    ],
  );
  const dateProjectOptions = useMemo(
    () =>
      decorateAvailabilityOptions(
        projectOptions ?? [],
        hasCompleteAvailability
          ? availability.projects.map(({ id }) => id)
          : undefined,
        dateMode,
        (option) => isTimeOffProjectCategory(option.projectCategory),
        recordsDaysWithoutWork,
      ),
    [
      availability.projects,
      dateMode,
      hasCompleteAvailability,
      projectOptions,
      recordsDaysWithoutWork,
    ],
  );
  const selectedClientId = client?.id ?? client?.value;
  const selectedProjectId = project?.id ?? project?.value;
  // Orders are per client. An available project of the same client that needs
  // an order (not a time-off project) proves the client's coverage, so a
  // selected project missing from the list is unavailable for another reason
  // (e.g. no project assignment), not for a missing order.
  const clientHasOrderCoverage = (clientId: number) =>
    availability.projects.some((available) => {
      if (available.clientId !== clientId) {
        return false;
      }
      const option = projectOptions?.find(
        ({ value }) => value === available.id,
      );
      return (
        option != null && !isTimeOffProjectCategory(option.projectCategory)
      );
    });
  const selectedValueNeedsOrder =
    hasCompleteAvailability &&
    selectedClientId != null &&
    (!availability.clients.some(({ id }) => id === selectedClientId) ||
      (selectedProjectId != null &&
        !availability.projects.some(({ id }) => id === selectedProjectId) &&
        !clientHasOrderCoverage(selectedClientId)));

  const isDayFree = useMemo(() => {
    return isTimeOffProjectCategory(project?.projectCategory);
  }, [project?.projectCategory]);

  const isDateBeforeToday = useMemo(() => {
    return new Date(entry.date) <= new Date();
  }, [entry.date]);

  useEffect(() => {
    if (startTime !== null && endTime !== null) {
      setIsEndingTimeBeforeStarting(startTime > endTime);
    }
  }, [startTime, endTime]);

  const isEndingTimeBeforeStartingTitle = intl.formatMessage({
    id: 'FULL_REPORT.WARNING_TOOLTIP.WRONG_ENDING_TIME',
  });

  const breakTime = useWatch({
    control,
    name: `days[${index}].breakTime`,
  });
  const savedMillis = useWatch({
    control,
    name: `days[${index}].savedMillis`,
  });
  const invalidWorkingHours =
    hoursOnly && !parseWorkingHours(workingHours).valid;

  // A B2B or civil-contract day without work (0 hours) needs no assignment
  // and may have no client or project, so they are neither required nor
  // flagged; any other row, including one without hours yet, still needs an
  // assignment.
  const isDayWithoutWork = isDayWithoutWorkRow(hoursOnly, workingHours);
  const showOrderWarning = selectedValueNeedsOrder && !isDayWithoutWork;
  // Only an empty past working day may have been forgotten; a saved 0-hour day
  // is reported.
  const isOverdue =
    !entry.parentId &&
    entry.dayOfWeek !== 'SATURDAY' &&
    entry.dayOfWeek !== 'SUNDAY' &&
    !entry.holiday &&
    !entry.startTime &&
    !entry.endTime &&
    !(hoursOnly && entry.hours !== '' && entry.hours != null) &&
    !isLeaveDay &&
    !entry.bankHoliday &&
    isDateBeforeToday;

  const hasPersistedDailyTime = entry.id != null;

  // Changed hours or times of a saved row that reports work are saved and
  // re-linked, for example a "Missing" row whose client got an assignment or
  // a 0-hour day that now has hours. The baseline is the row as loaded.
  const savedTimes = savedDay && getFullReportDayTimes(savedDay, hoursOnly);
  const editedTimes =
    savedDay &&
    getFullReportDayTimes(
      {
        ...savedDay,
        startTime,
        endTime,
        breakTime,
        hours: workingHours,
        savedMillis,
      } as unknown as FullReportDay,
      hoursOnly,
    );
  const workChanged =
    hasPersistedDailyTime &&
    Boolean(savedTimes && editedTimes) &&
    (editedTimes?.startTime !== savedTimes?.startTime ||
      editedTimes?.endTime !== savedTimes?.endTime ||
      editedTimes?.breakTime !== savedTimes?.breakTime ||
      editedTimes?.hours !== savedTimes?.hours);

  // A date out of scope (OFF, before the effective date, or an employment
  // contract that day) links no assignment on save, so its row never awaits
  // one and shows "—" in a month that has the column (B2A-01). A saved row
  // keeps what it stores.
  const orderApplies = dateMode !== 'OFF';

  // A changed client or project awaits the save only when the save keeps it:
  // the row reports work now, or reported it when loaded (clearing it is a
  // deletion).
  const isOrderPending =
    orderApplies &&
    (orderPending ||
      (clientOrProjectChanged && (savedReportsWork || reportsWorkNow)) ||
      (workChanged && reportsWorkNow));

  // The assignment a save would link (owner, 24.10, U10): the one covering
  // the date for the selected client, once the row reports work that needs
  // one. Absences and days without work need none.
  const pendingOrderNumber =
    orderApplies &&
    hasCompleteAvailability &&
    selectedClientId != null &&
    !isDayFree &&
    !isDayWithoutWork &&
    !invalidWorkingHours &&
    reportsWorkNow
      ? availability.clients.find(({ id }) => id === selectedClientId)
          ?.orderNumber
      : undefined;

  const deleteInsertedHours = () => {
    setValue(`days[${index}].startTime`, null, { shouldDirty: true });
    setValue(`days[${index}].endTime`, null, { shouldDirty: true });
    setValue(`days[${index}].breakTime`, null, { shouldDirty: true });
  };

  return (
    <TableRow
      key={`fullReportDay-${entry.id}`}
      ref={moment(entry.date).isSame(Date.now(), 'date') ? rowRef : undefined}
      index={index}
      $weekend={isFreeDay || isDayFree}
      $today={isToday}
      $attention={Boolean(entry.needsCorrection) && !isOrderPending}
    >
      <TableCell>
        <HiddenInput //this hidden input allows me to store id in form, thus obtaining it via getValues
          type="hidden"
          name={`days[${index}].id`}
          defaultValue={entry.id}
          ref={register()}
        />
        <HiddenInput
          type="hidden"
          name={`days[${index}].dayId`}
          defaultValue={entry.dayId}
          ref={register()}
        />
        <HiddenInput
          type="hidden"
          name={`days[${index}].parentId`}
          defaultValue={entry.parentId}
          ref={register()}
        />
        <HiddenInput
          type="hidden"
          name={`days[${index}].date`}
          defaultValue={entry.date}
          ref={register()}
        />
        {/* Kept as form state, not as a registered hidden input: the value of
            a hidden input is its defaultValue, which React restores on every
            render, so clearing it when the hours are edited would not stick. */}
        <Controller
          name={`days[${index}].savedMillis`}
          control={control}
          defaultValue={entry.savedMillis ?? ''}
          render={({ name, value }) => (
            <HiddenInput type="hidden" name={name} value={value ?? ''} />
          )}
        />
        <DateWrapper>
          {entry.parentId && <ArrowRight />}
          <ReportTypography>{`${entry.dayOfMonth} ${
            entry.dayOfWeek?.charAt(0) +
            entry.dayOfWeek?.slice(1, 3).toLowerCase()
          }`}</ReportTypography>
          {/* Icons follow the label, so day labels line up on every row. */}
          {availability.status === 'loading' && (
            <CircularProgress
              size={16}
              style={{ marginLeft: 5 }}
              aria-label={intl.formatMessage({
                id: 'ORDER_REPORTING.AVAILABILITY_LOADING',
              })}
            />
          )}
          {(showOrderWarning ||
            ['partial', 'unauthorized', 'error'].includes(
              availability.status,
            )) && (
            <WarningTooltip
              id={`order-reporting-${index}`}
              title={
                <HeaderTooltipText>
                  {intl.formatMessage({
                    id: showOrderWarning
                      ? 'ORDER_REPORTING.DATE_WARNING'
                      : `ORDER_REPORTING.AVAILABILITY_${availability.status.toUpperCase()}`,
                  })}
                </HeaderTooltipText>
              }
              placement="top"
              style={{ marginLeft: 0 }}
            >
              <OrderWarningIcon />
            </WarningTooltip>
          )}
          {!entry.parentId && entry.bankHoliday && (
            <WarningTooltip
              id="bankHolidayTooltip"
              title={
                <HeaderTooltipText>
                  {getBankHolidayTooltip(entry)}
                </HeaderTooltipText>
              }
              placement="top"
              style={{
                marginLeft: 0,
              }}
            >
              <InfoTooltipIcon />
            </WarningTooltip>
          )}
          {isOverdue && (
            <WarningTooltip
              id="overdueTooltip"
              title={
                <HeaderTooltipText>
                  {intl.formatMessage({
                    id: 'WORK_TIME_TAB.OVERDUE.TOOLTIP',
                  })}
                </HeaderTooltipText>
              }
              placement="top"
              style={{
                marginLeft: 0,
              }}
            >
              <WarningIcon />
            </WarningTooltip>
          )}
        </DateWrapper>
      </TableCell>
      {hoursOnly ? (
        <TableCell align="center">
          <ControlledTextField
            id="workingHours"
            name={`days[${index}].hours`}
            control={control}
            size="small"
            // A format hint, not a value: a saved 0-hour day shows "0".
            placeholder="h:mm"
            maxLength={8}
            defaultValue={isLeaveDay ? '' : entry.hours}
            isDisabled={isLeaveDay || isTimesheetDisabled}
            // Once the hours are edited the row sends the typed value, even if
            // it ends up showing the saved duration again; reloading the report
            // restores the saved duration.
            handleChange={() => setValue(`days[${index}].savedMillis`, '')}
            validate={invalidWorkingHours}
            rules={invalidWorkingHours ? {} : undefined}
            helperText={
              invalidWorkingHours
                ? intl.formatMessage({
                    id: 'FULL_REPORT.WORKING_HOURS_INVALID',
                  })
                : undefined
            }
          />
        </TableCell>
      ) : (
        <>
          <TableCell style={{ position: 'relative' }}>
            {(isInvalidWorktime || isEndingTimeBeforeStarting) && (
              <OverlappingHoursTooltip
                id="overlappingHoursTooltip"
                title={
                  <HeaderTooltipText>
                    {isInvalidWorktime
                      ? invalidWorktimeTitle
                      : isEndingTimeBeforeStartingTitle}
                  </HeaderTooltipText>
                }
                placement="top"
              >
                <WarningIcon />
              </OverlappingHoursTooltip>
            )}
            <ControlledTimePicker
              id="startTime"
              name={`days[${index}].startTime`}
              control={control}
              required
              defaultValue={entry.startTime}
              isDisabled={isLeaveDay || isTimesheetDisabled}
              showMidnight
              onInvalidWorktime={onInvalidWorktime}
              isError={isInvalidWorktime || isEndingTimeBeforeStarting}
              handleRefreshAfterTimeChange={handleRefreshAfterTimeChange(index)}
            />
          </TableCell>
          <TableCell>
            <ControlledTimePicker
              id="endTime"
              name={`days[${index}].endTime`}
              control={control}
              required
              defaultValue={entry.endTime}
              isDisabled={isLeaveDay || isTimesheetDisabled}
              showMidnight
              onInvalidWorktime={onInvalidWorktime}
              isError={isInvalidWorktime || isEndingTimeBeforeStarting}
              handleRefreshAfterTimeChange={handleRefreshAfterTimeChange(index)}
            />
          </TableCell>
          <TableCellWithIcon $smaller={!startTime && !endTime}>
            <ControlledTimePicker
              id="breakTime"
              name={`days[${index}].breakTime`}
              control={control}
              required
              defaultValue={entry.breakTime}
              isDisabled={isLeaveDay || isTimesheetDisabled}
              handleRefreshAfterTimeChange={handleRefreshAfterTimeChange(index)}
            />
            {(startTime || endTime) && (
              <ClearButton>
                <Tooltip
                  title={
                    <HeaderTooltipText>
                      {intl.formatMessage({
                        id: 'FULL_REPORT.DELETE_INSERTED_HOURS',
                      })}
                    </HeaderTooltipText>
                  }
                  placement="top"
                >
                  <ClearIcon fontSize="small" onClick={deleteInsertedHours} />
                </Tooltip>
              </ClearButton>
            )}
          </TableCellWithIcon>
          <TableCell align="center">
            <ControlledTextField
              id="manHours"
              name={`days[${index}].manHours`}
              control={control}
              defaultValue={!isLeaveDay && entry.manHours}
              isDisabled
              isDisabledTotalTime
            />
          </TableCell>
        </>
      )}
      {showMultiplier && (
        <TableCell align="center">
          <ControlledTextField
            id="multiplicator"
            name={`days[${index}].multiplicator`}
            control={control}
            defaultValue={
              entry?.projectBasicInfo?.multiplicator ??
              defaultProject?.multiplicator
            }
            isDisabled
            isDisabledTotalTime
          />
        </TableCell>
      )}
      <TableCell size="small">
        <ControlledSelect
          id="client"
          label="TABLE.HEAD.CLIENT"
          name={`days[${index}].client`}
          control={control}
          options={clientOptions}
          required={!isDayWithoutWork}
          isSmall
          defaultValue={checkWhatToRender(entry, 'client')}
          handleChange={handleClientChange(entry.dayId, index)}
          isDisabled={isTimesheetDisabled}
          isOptionDisabled={(option) =>
            option.status === 'INACTIVE' || isOrderUnavailable(option)
          }
          isTooltipEnabled
        />
      </TableCell>
      <TableCell>
        {/* A locked timesheet shows the full project name as the field's own
            tooltip; an extra info icon read as a warning (CR 24.10, B5). */}
        <Tooltip
          title={
            isTimesheetDisabled
              ? entry.projectBasicInfo?.name || defaultProject?.name || ''
              : ''
          }
          placement="top"
        >
          <div>
            <ControlledSelect
              id="project"
              label="TABLE.HEAD.PROJECT"
              name={`days[${index}].project`}
              control={control}
              options={dateProjectOptions}
              required={!isDayWithoutWork}
              isSmall
              isWider
              defaultValue={checkWhatToRender(entry, 'project')}
              isDisabled={isTimesheetDisabled}
              noOptionMessage={intl.formatMessage({
                id: 'WORK_TIME.SELECT_CLIENT',
              })}
              isOptionDisabled={(option) =>
                option.status === 'INACTIVE' ||
                option.reportingCustomData === true ||
                isOrderUnavailable(option)
              }
              handleChange={handleProjectChange(entry.dayId, index)}
              isTooltipEnabled={!isTimesheetDisabled}
            />
          </div>
        </Tooltip>
      </TableCell>
      {showOrder && (
        <OrderCell
          orderId={entry.orderId}
          orderNumber={entry.orderNumber}
          orderHours={entry.orderHours}
          orderDays={entry.orderDays}
          orderStatus={
            orderApplies || entry.orderId !== undefined
              ? entry.orderStatus
              : entry.orderStatus ?? 'NOT_IN_SCOPE'
          }
          needsCorrection={entry.needsCorrection}
          pending={isOrderPending}
          pendingOrderNumber={pendingOrderNumber}
        />
      )}
      <TableCell>
        <ControlledTextField
          id="comment"
          name={`days[${index}].comment`}
          control={control}
          required
          maxLength={255}
          size="small"
          defaultValue={entry.comment}
          isDisabled={isTimesheetDisabled}
        />
      </TableCell>
      <TableActionCell align="center">
        {entry.parentId ? (
          <Tooltip
            title={
              <HeaderTooltipText>
                {intl.formatMessage({
                  id: 'WORK_TIME_TAB.REMOVE_DAILY_PROJECT.TOOLTIP',
                })}
              </HeaderTooltipText>
            }
            placement="top"
          >
            <StyledTableIconButton
              id="removeRow"
              disabled={isTimesheetDisabled}
              onClick={deleteEntryFromDailytime(entry.dayId, index)}
            >
              <RemoveIcon />
            </StyledTableIconButton>
          </Tooltip>
        ) : (
          <Box display="flex">
            <Tooltip
              title={
                <HeaderTooltipText>
                  {intl.formatMessage({
                    id: 'WORK_TIME_TAB.ADD_DAILY_PROJECT.TOOLTIP',
                  })}
                </HeaderTooltipText>
              }
              placement="top"
            >
              <StyledTableIconButton
                id="addRow"
                disabled={
                  isLeaveDay || isTimesheetDisabled || !hasPersistedDailyTime
                }
                onClick={addEntryToDailytime(entry, index)}
              >
                <AddIcon />
              </StyledTableIconButton>
            </Tooltip>
            <Tooltip
              title={
                <HeaderTooltipText>
                  {intl.formatMessage({
                    id: 'WORK_TIME_TAB.MANAGE_UPLOADED_FILES.TOOLTIP',
                  })}
                </HeaderTooltipText>
              }
              placement="top"
            >
              <StyledTableIconButton
                id="attachments"
                disabled={isTimesheetDisabled || !hasPersistedDailyTime}
                onClick={handleFileUploadClick(entry)}
              >
                <UploadFileIcon />
                {entry.attachments.length !== 0 && (
                  <FileCounter>{entry.attachments.length}</FileCounter>
                )}
              </StyledTableIconButton>
            </Tooltip>
          </Box>
        )}
      </TableActionCell>
    </TableRow>
  );
};

export default memo(FullReportRow);
